// Your credentials (02-DESIGN/03-console/01-the-console.md § the shape: a
// person decides only what acts as them; § the surfaces: the toolkits this
// person linked and the records they pasted, with unlink and remove;
// 02-DESIGN/08-toolkits/03-linking.md § the namespace, § unlinking): the
// person's own paths under their namespace in the workspace's vault, listed
// without revealing a value, each the credential one toolkit spends as them.
// A row whose toolkit serves opens the toolkit's page: the row is the link,
// so its name is (§ conventions: a name is a link).
// A solution's person-scoped settings are listed beside them
// (02-DESIGN/14-solutions/01-the-manifest.md § settings and secrets).

import { subject } from "@impire-io/sdk";
import { useState } from "react";
import { Link } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { CODE_NO_SERVICE, isRefusal } from "../../platform/bus.ts";
import { Empty, NotAllowed, Notice, PageHead, Pending, useLoad, when } from "../../ui/components.tsx";
import { PERSON_TOOLKIT_SEGMENT, personToolkitPrefix, Scheme, type Toolkit } from "../../wire/toolkit.ts";
import { Table } from "../../ui/table.tsx";
import { PersonSettings } from "../solutions/PersonSettings.tsx";
import { useSurface } from "../surface.tsx";
import { DISCONNECT_CONSEQUENCE, UNLINK_CONSEQUENCE } from "../toolkits/state.ts";

/** Held is one credential this person holds: the toolkit it is for and what kind it is. */
export interface Held {
  path: string;
  workload: string;
  scheme: string;
  version: number;
  stored: string;
}

/**
 * heldOf reads a path of the person's namespace back:
 * `person.<principal>.toolkit.<workload>.<scheme>`; undefined for a path of
 * another shape, which this page does not draw.
 */
export function heldOf(path: string, version: number, stored: string): Held | undefined {
  const toks = subject.tokens(path);
  const [, , segment, workload, scheme] = toks;
  if (
    toks.length !== 5 ||
    segment !== PERSON_TOOLKIT_SEGMENT ||
    workload === undefined ||
    scheme === undefined
  ) {
    return undefined;
  }
  return { path, workload, scheme, version, stored };
}

/** kindWords is what a held credential is called. */
export function kindWords(scheme: string): string {
  switch (scheme) {
    case Scheme.oauthRefresh:
      return "your account, linked";
    case Scheme.token:
      return "a key you pasted";
    default:
      return scheme;
  }
}

export function CredentialsPage() {
  const s = useSurface();
  const principal = s.principal;
  const [held, reload] = useLoad(`credentials/${principal}`, () =>
    principal === ""
      ? Promise.resolve<Held[]>([])
      : s.bus
          .vault()
          .list(personToolkitPrefix(principal))
          .then((reply) =>
            reply.paths
              .map((e) => heldOf(e.path, e.version, e.stored_at))
              .filter((h): h is Held => h !== undefined),
          ),
  );
  const [toolkits] = useLoad("credentials/toolkits", () => s.bus.discoverToolkits());
  const [err, setErr] = useState<unknown>(undefined);
  const noVault = held.state === "refused" && isRefusal(held.err, CODE_NO_SERVICE);
  const byWorkload = new Map<string, Toolkit>();
  if (toolkits.state === "ready") {
    for (const tk of toolkits.value) {
      byWorkload.set(tk.workload, tk);
    }
  }
  const remove = (h: Held) => {
    setErr(undefined);
    s.bus
      .vault()
      .delete(h.path, true)
      .then(
        () => {
          reload();
        },
        (e: unknown) => {
          setErr(e);
        },
      );
  };
  return (
    <>
      <PageHead
        title="Your credentials"
        line="What acts as you, and nobody else: linked accounts and pasted keys."
      />
      {principal === "" && (
        <Notice>
          Your sign-in does not carry what this page needs yet. Sign in again once the workspace&apos;s
          sign-in has been updated.
        </Notice>
      )}
      {err !== undefined && <NotAllowed err={err} />}
      {held.state === "pending" && <Pending what="Reading what you hold" />}
      {held.state === "refused" && !noVault && <NotAllowed err={held.err} what="Your credentials" />}
      {(noVault || (held.state === "ready" && held.value.length === 0)) && (
        <Empty>
          Nothing acts as you yet. A toolkit you link or paste a key into under{" "}
          <Link className="text-link" to={tenantPath(s.tenant, "toolkits")}>
            Toolkits
          </Link>{" "}
          appears here.
        </Empty>
      )}
      {held.state === "ready" && held.value.length > 0 && (
        <Table
          label="Your credentials"
          rows={held.value}
          rowKey={(h) => h.path}
          sort={{ key: "toolkit", dir: "asc" }}
          to={(h) => {
            const tk = byWorkload.get(h.workload);
            return tk === undefined ? undefined : tenantPath(s.tenant, "toolkits", tk.name);
          }}
          columns={[
            {
              key: "toolkit",
              header: "Toolkit",
              width: "minmax(0, 1.4fr)",
              value: (h) => byWorkload.get(h.workload)?.name ?? h.workload,
              cell: (h) => {
                const tk = byWorkload.get(h.workload);
                return (
                  <span className="cell">
                    <span className={tk === undefined ? "cell-main mono" : "cell-main"}>
                      {tk === undefined ? h.workload : tk.name}
                    </span>
                    <span className="cell-sub">
                      {tk === undefined ? "not serving right now" : `${tk.tools.length} tools act as you`}
                    </span>
                  </span>
                );
              },
            },
            {
              key: "what",
              header: "What",
              width: "minmax(0, 1.2fr)",
              facet: "Kind",
              value: (h) => kindWords(h.scheme),
              cell: (h) => kindWords(h.scheme),
            },
            {
              key: "stored",
              header: "Stored",
              width: "170px",
              narrow: false,
              value: (h) => h.stored,
              text: (h) => when(h.stored),
              cell: (h) => <span className="small muted">{when(h.stored)}</span>,
            },
          ]}
          menu={(h) => {
            const linked = h.scheme === Scheme.oauthRefresh;
            const name = byWorkload.get(h.workload)?.name ?? h.workload;
            return [
              {
                label: linked ? "Unlink" : "Remove",
                danger: true,
                confirm: {
                  title: linked ? `Unlink ${name}?` : `Remove your key for ${name}?`,
                  consequence: linked ? UNLINK_CONSEQUENCE : DISCONNECT_CONSEQUENCE,
                },
                onSelect: () => {
                  remove(h);
                },
              },
            ];
          }}
        />
      )}
      <PersonSettings />
    </>
  );
}
